import { useTranslation } from 'react-i18next';
import { Controller, useWatch, type UseFormReturn } from 'react-hook-form';
import { format, parseISO } from 'date-fns';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { ReceiveConvoyContextDto } from '@/entities/receive';
import { CONFIRM_ARRIVAL_TEXT_MAX_LENGTH } from '@/features/receive-confirm-arrival';
import { DatePicker, FormField } from '@/shared/ui';

import type { ReceiveConvoyFormValues } from '../model/types';

interface ReceiveConvoyHeaderFieldsProps {
  form: UseFormReturn<ReceiveConvoyFormValues>;
  context: ReceiveConvoyContextDto;
}

/** `yyyy-MM-dd` ↔ `Date`, parsed as local time so the day never shifts. */
const parseDate = (value: string): Date | null => {
  if (!value) return null;
  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Appears with the first loss on any truck and is mandatory from then on — one
 * explanation for the convoy, as in the legacy header. Watches the quantities
 * from its own component so a keystroke in the table does not re-render the header.
 */
function ReceiveConvoyLossExplanation({ form }: { form: UseFormReturn<ReceiveConvoyFormValues> }) {
  const { t } = useTranslation();
  const shipments = useWatch({ control: form.control, name: 'shipments' });
  const hasLoss = (shipments ?? []).some((shipment) => shipment.lines.some((line) => line.lostQty > 0));

  if (!hasLoss) return null;

  return (
    <FormField
      label={t('receive.loss.comments.label', 'Explanation of Losses')}
      name="explanationOfLosses"
      error={form.formState.errors.explanationOfLosses?.message}
      required>
      <Textarea
        {...form.register('explanationOfLosses')}
        id="explanationOfLosses"
        rows={2}
        maxLength={CONFIRM_ARRIVAL_TEXT_MAX_LENGTH}
        placeholder={t('receive.loss.comments.label', 'Explanation of Losses')}
      />
    </FormField>
  );
}

/** Origin, destination and convoy (read-only), and the one receipt date of the whole convoy. */
export function ReceiveConvoyHeaderFields({ form, context }: ReceiveConvoyHeaderFieldsProps) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors }
  } = form;

  return (
    <div className="space-y-6">
      {/* `grid-cols-1` names the single narrow-screen column so truncating
          labels stay bounded — see the single receipt's header for the reason. */}
      <div className="grid grid-cols-1 gap-x-6 gap-y-7 sm:grid-cols-2 lg:grid-cols-4">
        <FormField label={t('journey.origin.label', 'Origin Location')} name="convoyOrigin">
          <Input id="convoyOrigin" value={context.originLocation || '—'} disabled readOnly />
        </FormField>

        <FormField label={t('convoy.destination.label', 'Destination')} name="convoyDestination">
          <Input id="convoyDestination" value={context.destinationLocation || '—'} disabled readOnly />
        </FormField>

        <FormField label={t('convoy.name.desc', 'Convoy')} name="convoyName">
          <Input id="convoyName" value={context.convoyName || '—'} disabled readOnly />
        </FormField>

        <FormField
          label={t('receive.receivedate.label', 'Actual Date of Receipt')}
          name="actualDateOfReceipt"
          error={errors.actualDateOfReceipt?.message}
          required>
          <Controller
            name="actualDateOfReceipt"
            control={control}
            render={({ field }) => (
              <DatePicker
                id="actualDateOfReceipt"
                value={parseDate(field.value)}
                onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
                dateFormat="dd/MMM/yyyy"
                minDate={parseDate(context.minReceiveDate) ?? undefined}
                maxDate={parseDate(context.maxReceiveDate) ?? undefined}
              />
            )}
          />
        </FormField>
      </div>

      <ReceiveConvoyLossExplanation form={form} />
    </div>
  );
}
